import React from "react";
import {
  Box,
  Card,
  Chip,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// ---------- Small Sparkline (no chart library needed) ----------
function Sparkline({ points = [5, 7, 6, 8, 7, 9, 8, 10], height = 52 }) {
  // Normalize points into SVG coordinates
  const w = 220;
  const h = height;
  const pad = 6;

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;

  const stepX = (w - pad * 2) / (points.length - 1);

  const d = points
    .map((p, i) => {
      const x = pad + i * stepX;
      const y = pad + (h - pad * 2) * (1 - (p - min) / range);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");

  // Area path
  const area = `${d} L ${w - pad} ${h - pad} L ${pad} ${h - pad} Z`;

  return (
    <Box sx={{ width: "100%", mt: 1 }}>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none">
        <path d={area} fill="currentColor" opacity="0.12" />
        <path d={d} fill="none" stroke="currentColor" strokeWidth="2.2" opacity="0.65" />
      </svg>
    </Box>
  );
}

// ---------- Vitals Card ----------
function VitalCard({
  title,
  value,
  unit,
  status = "Normal",
  iconBg = "#F3E6CF",
  icon = "💛",
  accent = "#1F3A3D",
  spark = [5, 7, 6, 8, 7, 9, 8, 10],
}) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
        background: "#fff",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            background: iconBg,
            display: "grid",
            placeItems: "center",
            fontSize: 20,
          }}
        >
          {icon}
        </Box>

        <Typography sx={{ fontWeight: 700, color: accent }}>{title}</Typography>
      </Box>

      {/* Value */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 1.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 30, color: accent }}>
          {value}
        </Typography>
        <Typography sx={{ color: "text.secondary", fontWeight: 600 }}>
          {unit}
        </Typography>
      </Box>

      {/* Status */}
      <Chip
        label={status}
        size="small"
        sx={{
          mt: 1,
          borderRadius: 2,
          fontWeight: 700,
          bgcolor: "rgba(0,0,0,0.06)",
        }}
      />

      {/* Sparkline */}
      <Box sx={{ color: accent }}>
        <Sparkline points={spark} />
      </Box>
    </Card>
  );
}

// ---------- Page ----------
export default function VitalsPage() {
  const theme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down("sm"));

  const vitals = [
    {
      title: "Blood Sugar",
      value: 80,
      unit: "mg / dL",
      status: "Normal",
      icon: "🩸",
      iconBg: "#F2E6D5",
      accent: "#244A4E",
      spark: [6, 6.5, 6.2, 7.2, 6.8, 7.6, 7.1, 8.0],
    },
    {
      title: "Heart rate",
      value: 98,
      unit: "BPM",
      status: "Normal",
      icon: "❤️",
      iconBg: "#F6D9DD",
      accent: "#6A2C3A",
      spark: [5.8, 5.6, 5.9, 6.3, 6.0, 6.6, 6.2, 6.9],
    },
    {
      title: "Blood Pressure",
      value: "90",
      unit: "/ 72 mmhg",
      status: "Normal",
      icon: "💧",
      iconBg: "#DDF3F7",
      accent: "#1F5B69",
      spark: [6.2, 6.5, 6.1, 6.4, 6.8, 6.6, 6.9, 7.0],
    },
    {
      title: "Hemoglobin",
      value: 14,
      unit: "g/dl",
      status: "Normal",
      icon: "🧫",
      iconBg: "#F5D7DB",
      accent: "#7A2D3B",
      spark: [6.0, 6.2, 6.1, 6.4, 6.3, 6.6, 6.5, 6.8],
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 4,
        background: "linear-gradient(180deg, #FFFFFF 0%, #FBF7F4 100%)",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* Top Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: 22, md: 28 },
            color: "#D86755",
          }}
        >
          Vital Sign&apos;s
        </Typography>

        <FormControl size={isDownSm ? "small" : "medium"} sx={{ minWidth: 140 }}>
          <Select
            value={"Jan 2024"}
            onChange={() => {}}
            sx={{
              borderRadius: 3,
              bgcolor: "#fff",
              fontWeight: 700,
            }}
          >
            <MenuItem value="Jan 2024">Jan 2024</MenuItem>
            <MenuItem value="Feb 2024">Feb 2024</MenuItem>
            <MenuItem value="Mar 2024">Mar 2024</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Cards */}
      <Grid container spacing={2}>
        {vitals.map((v) => (
          <Grid key={v.title} item xs={12} sm={6} md={3}>
            <VitalCard {...v} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}